import type { CSSProperties, ReactNode, SVGProps } from 'react';

/* ============================================================
   Icons — single-stroke line family, 1.5px stroke.
   ============================================================ */

type IconProps = {
  size?: number;
  stroke?: number;
  style?: CSSProperties;
  className?: string;
};

const baseIconProps = (size: number, stroke: number, style?: CSSProperties): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: stroke,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  style: { display: 'block', flexShrink: 0, ...style },
  'aria-hidden': true,
});

export const ReticleIcon = ({ size = 20, stroke = 1.5, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

export const HelmetIcon = ({ size = 22, style }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ display: 'block', flexShrink: 0, ...style }}
  >
    <path
      d="M5 11c0-4 3-8 8-8 3 0 5 1.5 5.5 2.5-2 0-3.5 1-4 2.5h2c-.5 1-1.5 1.5-3 1.5.5 1 1.5 1.5 2.5 1.5l-1 2c-1.5 0-2.5-.5-3-1l-.5 4.5L8 18l-3-2c0-2 0-3 0-5"
      fill="currentColor"
    />
  </svg>
);

export const ChevronDownIcon = ({ size = 14, stroke = 1.5, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const KeyboardIcon = ({ size = 14, stroke = 1.5, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <path d="M7 10h.5M11 10h.5M15 10h.5M7 14h10" />
  </svg>
);

export const LogOutIcon = ({ size = 14, stroke = 1.5, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
    <path d="M10 17 4 12l6-5M4 12h12" />
  </svg>
);

export const SettingsIcon = ({ size = 14, stroke = 1.5, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2M12 19v2M5 12H3M21 12h-2M6 6 4.5 4.5M19.5 19.5 18 18M6 18l-1.5 1.5M19.5 4.5 18 6" />
  </svg>
);

export const CheckIcon = ({ size = 14, stroke = 2, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <path d="m4 12 5 5 11-11" />
  </svg>
);

export const PhoneIcon = ({ size = 16, stroke = 1.5, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2z" />
  </svg>
);

export const PhoneEndIcon = ({ size = 16, stroke = 1.5, style }: IconProps) => (
  <svg {...baseIconProps(size, stroke, style)}>
    <path d="M3 12c5-4 13-4 18 0v3l-3 1-1-2c-3-1.5-7-1.5-10 0l-1 2-3-1z" />
    <path d="M5 17 19 5" />
  </svg>
);

/* ============================================================
   BrandMark — helmet inside reticle + optional wordmark
   ============================================================ */

export const BrandMark = ({
  size = 28,
  withWordmark = true,
}: {
  size?: number;
  withWordmark?: boolean;
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: 'var(--brand)' }}>
    <span style={{ position: 'relative', width: size, height: size, display: 'inline-flex' }}>
      <span style={{ position: 'absolute', inset: 0 }}>
        <ReticleIcon size={size} stroke={1.4} />
      </span>
      <span style={{ position: 'absolute', inset: size * 0.16 }}>
        <HelmetIcon size={size * 0.68} />
      </span>
    </span>
    {withWordmark && (
      <span
        style={{
          fontWeight: 600,
          letterSpacing: '0.06em',
          fontSize: 15,
          color: 'var(--brand)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        A.R.E.S
      </span>
    )}
  </span>
);

/* ============================================================
   StatusPill
   ============================================================ */

export type PillTone = 'ok' | 'warn' | 'error' | 'info' | 'neutral' | 'teal';

export const StatusPill = ({
  tone = 'neutral',
  pulsing = false,
  dot = true,
  children,
}: {
  tone?: PillTone;
  pulsing?: boolean;
  dot?: boolean;
  children: ReactNode;
}) => (
  <span className={`pill pill--${tone}${pulsing ? ' pill--reconnecting' : ''}`}>
    {dot && <span className="dot" aria-hidden="true" />}
    <span>{children}</span>
  </span>
);

/* ============================================================
   Avatar
   ============================================================ */

export const Avatar = ({
  name = '',
  presence,
  onClick,
  ariaLabel,
}: {
  name?: string;
  presence?: 'on' | 'away' | 'off';
  onClick?: () => void;
  ariaLabel?: string;
}) => {
  const initials =
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  const presenceClass =
    presence === 'away' ? 'avatar__presence--away' : presence === 'off' ? 'avatar__presence--off' : '';
  return (
    <button
      type="button"
      className="avatar"
      onClick={onClick}
      aria-label={ariaLabel ?? `Account: ${name || 'Operator'}`}
    >
      {initials}
      {presence && <span className={`avatar__presence ${presenceClass}`} aria-hidden="true" />}
    </button>
  );
};

/* ============================================================
   Kbd
   ============================================================ */

export const Kbd = ({ children }: { children: ReactNode }) => <span className="kbd">{children}</span>;

/* ============================================================
   Wait helpers
   ============================================================ */

export function waitStatus(ms: number): 'fresh' | 'aging' | 'urgent' {
  if (ms < 30_000) return 'fresh';
  if (ms < 120_000) return 'aging';
  return 'urgent';
}

export function formatWait(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r.toString().padStart(2, '0')}s`;
}
